import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { Button } from "@tellescope/ui";
import { useVideoCalling } from "../hooks/useVideoCallingContext";
import { useMeetingOperations } from "../hooks/useMeetingOperations";
import { useActiveScreenShare } from "../hooks/useActiveScreenShare";
import { VideoControls } from "./VideoControls";
import { ParticipantGrid } from "./ParticipantGrid";
import { VideoParticipant } from "./VideoParticipant";
import { LocalCameraTile } from "./LocalCameraTile";
import { ScreenShareStage } from "./ScreenShareStage";
import type { VideoRoomViewProps } from "../types";

/**
 * VideoRoomView - Active video call interface.
 *
 * Shows the participant grid normally, or a presentation layout (shared screen on a large stage
 * with camera tiles in a filmstrip) whenever someone is screen sharing.
 */
export function VideoRoomView({
  userRole,
  showEndForAll = false,
  localUser,
  resolveParticipantIdentity,
}: VideoRoomViewProps) {
  const { t } = useTranslation("videoCalling");
  const videoCalling = useVideoCalling();
  const { leaveMeeting, endMeeting } = useMeetingOperations();
  const activeScreenShare = useActiveScreenShare();

  const { participants } = videoCalling;

  const handleLeave = () => {
    leaveMeeting();
  };

  const handleEndForAll = async () => {
    try {
      await endMeeting();
    } catch (error) {
      console.error("Failed to end meeting:", error);
    }
  };

  const hasParticipants = participants.length > 0;

  const sharerName = activeScreenShare?.sharerIdentity
    ? resolveParticipantIdentity?.(activeScreenShare.sharerIdentity)
    : undefined;

  return (
    <div className="flex flex-col flex-1 bg-gray-900">
      {/* Video area */}
      <div className="flex-1 relative overflow-hidden">
        {/* Screen share error banner */}
        {videoCalling.screenShareError && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 bg-red-500/90 text-white text-sm px-3 py-1.5 rounded-lg shadow-lg max-w-[90%]">
            <span>{videoCalling.screenShareError}</span>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => videoCalling.setScreenShareError(null)}
              className="h-5 w-5 shrink-0 !text-white hover:!bg-white/20"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}

        {activeScreenShare ? (
          /* Presentation mode: shared screen on stage, cameras in a filmstrip */
          <div className="absolute inset-0 flex flex-col">
            <div className="flex-1 min-h-0 p-2">
              <ScreenShareStage
                track={activeScreenShare.track}
                isLocal={activeScreenShare.isLocal}
                sharerName={sharerName}
              />
            </div>
            <div className="flex gap-2 p-2 overflow-x-auto shrink-0">
              <div className="relative w-40 h-28 shrink-0 rounded-lg overflow-hidden">
                <LocalCameraTile localUser={localUser} avatarSize="sm" />
                <div className="absolute bottom-1 left-1 bg-black/60 px-1.5 py-0.5 rounded text-white text-xs">
                  {t("You")}
                </div>
              </div>
              {participants.map((participant) => (
                <div key={participant.sid} className="w-40 h-28 shrink-0">
                  <VideoParticipant participant={participant} resolveIdentity={resolveParticipantIdentity} />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* Local video - full screen when alone, PIP when others present */}
            {hasParticipants ? (
              <div className="absolute bottom-4 right-4 w-40 h-28 rounded-lg shadow-lg z-10 overflow-hidden">
                <LocalCameraTile localUser={localUser} avatarSize="sm" />
              </div>
            ) : (
              <div className="absolute inset-0">
                <LocalCameraTile localUser={localUser} avatarSize="lg" />
              </div>
            )}

            {/* Remote participants grid or waiting message overlay */}
            {hasParticipants ? (
              <ParticipantGrid participants={participants} resolveParticipantIdentity={resolveParticipantIdentity} />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="text-gray-400 bg-black/50 px-4 py-2 rounded-lg">
                  {t("Waiting for others to join...")}
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Control bar */}
      <VideoControls
        userRole={userRole}
        showEndForAll={showEndForAll}
        onLeave={handleLeave}
        onEndForAll={handleEndForAll}
      />
    </div>
  );
}
